import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid,
  TextField, MenuItem, InputAdornment, Typography, Divider, CircularProgress,
} from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';

import { useLanguage } from '../../contexts/LanguageContext';
import { jourCivilAujourdhui } from '../../utils/dates';
import { formatCurrency, getCurrencySymbol } from '../../utils/currency';
import { numeroAffichable } from '../../utils/numeroCommande';
import {
  TYPES_REGLEMENT_ORDONNES, TYPE_REGLEMENT_DEFAUT, libelleTypeReglement,
} from '../../constants/paymentType';

/**
 * Ajouter un paiement à une commande — UN SEUL dialogue pour les deux écrans.
 *
 * 🔴 IL Y EN AVAIT DEUX, ET ILS AVAIENT DÉJÀ DIVERGÉ. Mesuré au lot RU4-a :
 * `Accounting.js:332-467` (136 lignes) et `OrderDetailView.js:840-940` (101
 * lignes) — **deux blocs JSX indépendants**, plus deux jeux d'état et deux
 * `handleSubmitPayment`. Le `diff` à indentation neutralisée donnait **trois**
 * différences et *tout le reste identique ligne à ligne* : le titre, un bloc de
 * 33 lignes de chiffres présent d'un côté seulement, et le pré-remplissage du
 * montant.
 *
 * ⚠️ ET LE CHEMIN LE PLUS NATUREL ÉTAIT LE PLUS PAUVRE : on ajoute un paiement
 * depuis le DÉTAIL de la commande, et c'est là qu'il n'y avait ni chiffres ni
 * montant pré-rempli. La copie riche vivait sur l'écran spécialisé, où
 * l'information était déjà à l'écran. *Une divergence ne se répartit pas au
 * hasard : elle enrichit l'endroit où l'on travaillait ce jour-là.*
 *
 * 🟢 C'ÉTAIT DONC UNE EXTRACTION, PAS UNE HARMONISATION. Les trois différences
 * se DÉDUISENT de la commande : le titre porte son numéro, le montant se
 * pré-remplit avec ce qu'elle doit, les chiffres sont les siens. Corriger les
 * deux copies séparément aurait recréé l'écart au lot suivant — c'est la leçon
 * de la condition recopiée, de la chasse fixe et du vocabulaire de règlement.
 */
const DialogueAjoutPaiement = ({
  commande, ouvert, onFermer, onEnregistre, showSnackbar,
}) => {
  const { t } = useLanguage();
  const [enCours, setEnCours] = useState(false);

  const devise = commande?.currency || 'EUR';
  const symbole = getCurrencySymbol(devise);

  // 🔴 LE RESTE DÛ EST SERVI PAR LE SERVEUR quand il l'est (liste, Comptabilité),
  // et se déduit sinon. Il n'est PAS recalculé à partir d'une liste de paiements
  // que ce dialogue n'a pas : c'est ce qui garantit que le montant pré-rempli est
  // celui de la ligne qu'on vient de cliquer.
  const resteDu = commande?.remaining_amount != null
    ? Number(commande.remaining_amount)
    : Math.max(0, (Number(commande?.total_payable ?? commande?.total_final) || 0)
                  - (Number(commande?.total_paid) || 0));

  const formulaireVierge = () => ({
    amount: resteDu > 0 ? resteDu.toFixed(2) : '',
    payment_date: jourCivilAujourdhui(),
    payment_type: TYPE_REGLEMENT_DEFAUT,
    comment: '',
    invoice_photo: null,
  });

  const [donnees, setDonnees] = useState(formulaireVierge);

  // ⚠️ LE PRÉ-REMPLISSAGE SE REFAIT À CHAQUE OUVERTURE, et pas seulement au
  // montage : le dialogue est monté une fois par écran et rouvert sur des
  // commandes différentes. Sans cette resynchronisation, la deuxième ouverture
  // proposerait le montant de la PREMIÈRE commande — un chiffre juste, sur la
  // mauvaise ligne, donc invisible à la relecture.
  useEffect(() => {
    if (ouvert) setDonnees(formulaireVierge());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, commande?.id, resteDu]);

  const surChangement = (e) => {
    const { name, value, files } = e.target;
    setDonnees((prev) => (name === 'invoice_photo'
      ? { ...prev, invoice_photo: files[0] || null }
      : { ...prev, [name]: value }));
  };

  const enregistrer = async () => {
    if (!donnees.amount || parseFloat(donnees.amount) <= 0) {
      if (showSnackbar) showSnackbar(t('orders.paymentAmountRequired'), 'error');
      return;
    }
    try {
      setEnCours(true);
      // La charge utile est construite PAR ÉNUMÉRATION, jamais par étalement de
      // l'état : c'est la leçon du correctif de RU3-c, où un `{...data}` renvoyait
      // au serveur des champs qu'il avait lui-même calculés.
      const charge = new FormData();
      charge.append('amount', donnees.amount);
      charge.append('payment_date', donnees.payment_date);
      charge.append('payment_type', donnees.payment_type);
      if (donnees.comment) charge.append('comment', donnees.comment);
      if (donnees.invoice_photo) charge.append('invoice_photo', donnees.invoice_photo);

      await onEnregistre(charge);
      if (showSnackbar) showSnackbar(t('orders.paymentCreated'), 'success');
      onFermer();
    } catch (err) {
      console.error('Erreur lors de la création du paiement:', err);
      if (showSnackbar) {
        showSnackbar(err.response?.data?.error || t('orders.paymentCreateError'), 'error');
      }
    } finally {
      setEnCours(false);
    }
  };

  const chiffre = (libelle, valeur, teinte) => (
    <Grid item xs={12} sm={4}>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">{libelle}</Typography>
        <Typography variant="h6" color={teinte || 'text.primary'}>
          {formatCurrency(valeur || 0, devise)}
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <Dialog open={ouvert} onClose={onFermer} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span">{t('orders.addPayment')}</Typography>
        {commande?.order_number && (
          <Typography variant="body2" color="text.secondary">
            {numeroAffichable(commande.order_number)}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {/* 🔴 LES TROIS CHIFFRES SONT LE COMPORTEMENT COMMUN, plus le privilège de
            l'écran de Comptabilité. Ils répondent à la question qu'on se pose en
            ouvrant ce dialogue — combien reste-t-il ? — et l'écran de détail ne
            les avait pas.
            ⚠️ APLATS RETIRÉS. Les cartes portaient des fonds `success.light` et
            `warning.light` PLEINS sur toute leur surface : mesuré, `success.light`
            vaut `#4caf50` en mode clair — un vert SATURÉ, pas une teinte. Sur une
            grande carte, un aplat vif pèse plus que le chiffre qu'il porte. La
            couleur reste, portée par le TEXTE, où elle qualifie sans dominer. */}
        <Grid container spacing={2} sx={{ mb: 2, mt: 0 }}>
          {chiffre(t('orders.finalTotal'), commande?.total_payable ?? commande?.total_final)}
          {chiffre(t('orders.totalPaid'), commande?.total_paid, 'success.main')}
          {chiffre(t('orders.remainingAmount'), resteDu,
                   resteDu > 0 ? 'warning.main' : 'success.main')}
        </Grid>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('orders.amount')}
            name="amount"
            type="number"
            value={donnees.amount}
            onChange={surChangement}
            fullWidth
            autoFocus
            InputProps={{ startAdornment: <InputAdornment position="start">{symbole}</InputAdornment> }}
            inputProps={{ min: 0, step: 0.01 }}
            helperText={resteDu > 0
              ? `${t('orders.remainingAmount')} : ${formatCurrency(resteDu, devise)}`
              : undefined}
          />
          <TextField
            label={t('orders.paymentDate')}
            name="payment_date"
            type="date"
            value={donnees.payment_date}
            onChange={surChangement}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          {/* ⚠️ LA LISTE VIENT DU VOCABULAIRE PARTAGÉ, DANS SON ORDRE. Les cinq
              codes sont fermés par le serveur, qui refuse le reste en 400. */}
          <TextField
            label={t('orders.paymentType')}
            name="payment_type"
            select
            value={donnees.payment_type}
            onChange={surChangement}
            fullWidth
          >
            {TYPES_REGLEMENT_ORDONNES.map((code) => (
              <MenuItem key={code} value={code}>{libelleTypeReglement(code, t)}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('orders.comment')}
            name="comment"
            value={donnees.comment}
            onChange={surChangement}
            multiline
            rows={3}
            fullWidth
          />
          <Box>
            <input
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              id="piece-jointe-paiement"
              type="file"
              name="invoice_photo"
              onChange={surChangement}
            />
            <label htmlFor="piece-jointe-paiement">
              <Button variant="outlined" component="span" startIcon={<AttachFileIcon />} fullWidth>
                {donnees.invoice_photo ? donnees.invoice_photo.name : t('orders.uploadInvoice')}
              </Button>
            </label>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFermer}>{t('common.cancel')}</Button>
        <Button
          onClick={enregistrer}
          variant="contained"
          disabled={enCours || !donnees.amount}
          startIcon={enCours ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {enCours ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogueAjoutPaiement;
